import type { ProjectedState } from '../../utils/strategyCalculations';
import { COLORS } from '../../constants/theme';

interface OutcomesPanelProps {
  projected: ProjectedState | null;
  density?: 'compact' | 'comfortable';
}

function formatNum(n: number): string {
  return Math.floor(n).toLocaleString();
}

function formatDecimal(n: number, digits: number = 2): string {
  return n.toFixed(digits);
}

function netColor(n: number): string {
  if (n > 0) return 'text-green-400';
  if (n < 0) return 'text-red-400';
  return '';
}

function formatNet(n: number): string {
  if (n >= 0) return `+${formatNum(n)}`;
  return `\u2212${formatNum(Math.abs(n))}`;
}

function SectionHeader({ children, comfortable }: { children: React.ReactNode; comfortable: boolean }) {
  return (
    <div
      className={`${comfortable ? 'text-sm' : 'text-xs'} uppercase tracking-wider mb-2 font-semibold`}
      style={{ color: COLORS.text.tertiary }}
    >
      {children}
    </div>
  );
}

function Row({ label, value, color, comfortable }: { label: string; value: string; color?: string; comfortable: boolean }) {
  return (
    <div className={`flex justify-between ${comfortable ? 'py-1' : 'py-px'}`}>
      <span>{label}</span>
      <span className={`font-mono ${color || ''}`} style={color ? undefined : { color: COLORS.text.secondary }}>{value}</span>
    </div>
  );
}

function SubHeader({ children, color }: { children: React.ReactNode; color?: string }) {
  return (
    <div className="text-sm font-medium mt-2 mb-1" style={{ color }}>
      {children}
    </div>
  );
}

export function OutcomesPanel({ projected, density = 'compact' }: OutcomesPanelProps) {
  const comfortable = density === 'comfortable';

  if (!projected) {
    return (
      <div className="rounded-lg p-4" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
        <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: COLORS.text.tertiary }}>
          Projected Outcome
        </h3>
        <p className="text-sm" style={{ color: COLORS.text.muted }}>Load a template or set province data to see projections.</p>
      </div>
    );
  }

  const { gold, food, runes, offense, defense, totalLand } = projected;
  const opa = totalLand > 0 ? offense.modified / totalLand : 0;
  const dpa = totalLand > 0 ? defense.modified / totalLand : 0;

  return (
    <div className="rounded-lg p-4 space-y-4" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
      <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: COLORS.text.tertiary }}>
        Projected Outcome
      </h3>

      {/* 1. Population */}
      <div className={comfortable ? 'text-sm' : 'text-xs'} style={{ color: COLORS.text.tertiary }}>
        <SectionHeader comfortable={comfortable}>Population</SectionHeader>
        <Row label="Peasants" value={formatNum(projected.peasants)} comfortable={comfortable} />
        <Row label="Army" value={formatNum(projected.army)} comfortable={comfortable} />
        <Row label="Wizards" value={formatNum(projected.wizards)} comfortable={comfortable} />
        <div className="mt-1 pt-1" style={{ borderTop: `1px solid ${COLORS.border}` }}>
          <Row label="Total" value={formatNum(projected.population)} comfortable={comfortable} />
        </div>
      </div>

      {/* 2. Economy */}
      <div className={comfortable ? 'text-sm' : 'text-xs'} style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: '1rem', color: COLORS.text.tertiary }}>
        <SectionHeader comfortable={comfortable}>Economy</SectionHeader>

        <Row label="Building Efficiency" value={`${(projected.buildingEfficiency * 100).toFixed(1)}%`} comfortable={comfortable} />

        {/* Gold */}
        <SubHeader>Gold (gc)</SubHeader>
        <div className="space-y-px pl-2">
          <Row label="Income" value={formatNum(gold.totalIncome)} comfortable={comfortable} />
          <Row label="Wages Cost" value={`\u2212${formatNum(gold.militaryWages)}`} color="text-red-400" comfortable={comfortable} />
          <Row label="Draft Cost" value={`\u2212${formatNum(gold.draftCost)}`} color="text-red-400" comfortable={comfortable} />
          <Row label="Science Cost" value={`\u2212${formatNum(gold.scienceCost)}`} color="text-red-400" comfortable={comfortable} />
          <div className="pt-0.5 mt-0.5" style={{ borderTop: `1px solid ${COLORS.border}` }}>
            <Row label="Net" value={formatNet(gold.net)} color={netColor(gold.net)} comfortable={comfortable} />
          </div>
        </div>

        {/* Food */}
        <SubHeader>Food (bushels)</SubHeader>
        <div className="space-y-px pl-2">
          <Row label="Food Produced" value={formatNum(food.production)} comfortable={comfortable} />
          <Row label="Food Consumed" value={`\u2212${formatNum(food.consumption)}`} color="text-red-400" comfortable={comfortable} />
          <div className="pt-0.5 mt-0.5" style={{ borderTop: `1px solid ${COLORS.border}` }}>
            <Row label="Net" value={formatNet(food.net)} color={netColor(food.net)} comfortable={comfortable} />
          </div>
        </div>

        {/* Runes */}
        <SubHeader>Runes (runes)</SubHeader>
        <div className="space-y-px pl-2">
          <Row label="Runes Produced" value={formatNum(runes.production)} comfortable={comfortable} />
          <div className="pt-0.5 mt-0.5" style={{ borderTop: `1px solid ${COLORS.border}` }}>
            <Row label="Net" value={formatNet(runes.net)} color={netColor(runes.net)} comfortable={comfortable} />
          </div>
        </div>
      </div>

      {/* 3. Military */}
      <div className={comfortable ? 'text-sm' : 'text-xs'} style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: '1rem', color: COLORS.text.tertiary }}>
        <SectionHeader comfortable={comfortable}>Military</SectionHeader>

        {/* Offense */}
        <SubHeader>Offense</SubHeader>
        <div className="space-y-px pl-2">
          <Row label="Raw Offense" value={formatNum(offense.raw)} comfortable={comfortable} />
          <Row label="OME" value={formatDecimal(offense.me)} comfortable={comfortable} />
          <div className="pt-0.5 mt-0.5" style={{ borderTop: `1px solid ${COLORS.border}` }}>
            <Row label="Modified Offense" value={formatNum(offense.modified)} comfortable={comfortable} />
          </div>
        </div>

        {/* Defense */}
        <SubHeader>Defense</SubHeader>
        <div className="space-y-px pl-2">
          <Row label="Raw Defense" value={formatNum(defense.raw)} comfortable={comfortable} />
          <Row label="DME" value={formatDecimal(defense.me)} comfortable={comfortable} />
          <div className="pt-0.5 mt-0.5" style={{ borderTop: `1px solid ${COLORS.border}` }}>
            <Row label="Modified Defense" value={formatNum(defense.modified)} comfortable={comfortable} />
          </div>
        </div>

        {/* Per-Acre Stats */}
        <div className="grid grid-cols-2 gap-2 mt-3">
          <div className="rounded p-2" style={{ backgroundColor: COLORS.cardAlt }}>
            <div className="mb-0.5" style={{ color: COLORS.text.tertiary }}>TPA</div>
            <div className="font-mono text-sm" style={{ color: COLORS.text.primary }}>{formatDecimal(projected.tpa)}</div>
          </div>
          <div className="rounded p-2" style={{ backgroundColor: COLORS.cardAlt }}>
            <div className="mb-0.5" style={{ color: COLORS.text.tertiary }}>WPA</div>
            <div className="font-mono text-sm" style={{ color: COLORS.text.primary }}>{formatDecimal(projected.wpa)}</div>
          </div>
          <div className="rounded p-2" style={{ backgroundColor: COLORS.cardAlt }}>
            <div className="mb-0.5" style={{ color: COLORS.text.tertiary }}>OPA</div>
            <div className="font-mono text-sm" style={{ color: COLORS.text.primary }}>{formatDecimal(opa)}</div>
          </div>
          <div className="rounded p-2" style={{ backgroundColor: COLORS.cardAlt }}>
            <div className="mb-0.5" style={{ color: COLORS.text.tertiary }}>DPA</div>
            <div className="font-mono text-sm" style={{ color: COLORS.text.primary }}>{formatDecimal(dpa)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
